import React, { useState } from 'react';
import {
  Search,
  Bell,
  CheckCircle2,
  AlertTriangle,
  Activity,
  Layers,
  Lock,
  Network,
  FileText,
  RefreshCw,
  Users,
  TrendingUp,
  DollarSign,
  Database,
  ShieldAlert,
  Sliders,
  HelpCircle,
  LogOut,
  ChevronRight,
  Plus,
  Server,
  ShieldCheck,
  Check,
  ExternalLink,
  Clock,
  Filter,
  X,
  Cpu,
  HardDrive,
  GitBranch,
  Terminal,
  Play,
  RotateCcw
} from 'lucide-react';
import { AppView } from '../../types';

interface SystemHealthDeploymentsProps {
  onNavigateToView: (view: AppView) => void;
  onOpenSearch?: () => void;
}

interface ServiceHealthCard {
  id: string;
  name: string;
  status: 'Operational' | 'Degraded' | 'Down';
  latency: string;
  load: string;
  isDegraded?: boolean;
}

interface InfraMetric {
  label: string;
  value: string;
  subtext: string;
  percentage: number;
  statusColor: 'emerald' | 'amber' | 'red' | 'blue';
}

interface DeploymentRecord {
  id: string;
  timestampUtc: string;
  environment: 'Production' | 'Staging' | 'Development';
  version: string;
  status: 'Success' | 'In Progress' | 'Failed' | 'Canary';
  branch: string;
  actor: string;
  commitSha: string;
  canRollback: boolean;
  rollbackVersion?: string;
}

export const SystemHealthDeployments: React.FC<SystemHealthDeploymentsProps> = ({
  onNavigateToView,
  onOpenSearch
}) => {
  const [lastUpdated, setLastUpdated] = useState<string>('Just now');
  const [selectedDomain, setSelectedDomain] = useState<string>('operations');
  const [envFilter, setEnvFilter] = useState<'ALL' | 'Production' | 'Staging' | 'Development'>('ALL');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showNotificationsPopover, setShowNotificationsPopover] = useState<boolean>(false);
  const [showNewDeploymentModal, setShowNewDeploymentModal] = useState<boolean>(false);
  const [selectedRollback, setSelectedRollback] = useState<DeploymentRecord | null>(null);

  // New Deployment Form State
  const [deployEnv, setDeployEnv] = useState<'Staging' | 'Production'>('Staging');
  const [deployBranch, setDeployBranch] = useState<string>('release/2.5.2');
  const [deployNotes, setDeployNotes] = useState<string>('');

  // Service Health Data (Actual telemetry)
  const [services, setServices] = useState<ServiceHealthCard[]>([
    { id: 'web-app', name: 'Web Application', status: 'Operational', latency: '42ms', load: '34%' },
    { id: 'public-api', name: 'Public API', status: 'Operational', latency: '18ms', load: '62%' },
    { id: 'auth', name: 'Authentication', status: 'Operational', latency: '115ms', load: '28%' },
    { id: 'postgresql', name: 'PostgreSQL DB', status: 'Degraded', latency: '845ms', load: '92%', isDegraded: true },
    { id: 'redis-queue', name: 'Redis / Queue', status: 'Operational', latency: '2ms', load: '12%' },
    { id: 'workers', name: 'Workers', status: 'Operational', latency: '14ms', load: '45%' },
    { id: 'fin-ingestion', name: 'Financial Data Ingestion', status: 'Operational', latency: '88ms', load: '19%' },
    { id: 'ai-gateway', name: 'AI Gateway', status: 'Operational', latency: '160ms', load: '22%' }
  ]);

  // Infrastructure Metrics (Real available telemetry)
  const infraMetrics: InfraMetric[] = [
    { label: 'CPU Usage', value: '48%', subtext: '8 cores allocated', percentage: 48, statusColor: 'emerald' },
    { label: 'Memory Usage', value: '612 MB', subtext: 'of 1,024 MB RAM', percentage: 59.7, statusColor: 'emerald' },
    { label: 'Disk Usage', value: '14.2 GB', subtext: 'of 50.0 GB volume', percentage: 28.4, statusColor: 'emerald' },
    { label: 'DB Connections', value: '92 / 100', subtext: 'Near pool capacity', percentage: 92, statusColor: 'amber' },
    { label: 'Queue Depth', value: '0 pending', subtext: 'Async worker pool OK', percentage: 0, statusColor: 'emerald' },
    { label: 'Error Rate (5xx)', value: '0.02%', subtext: 'SLA target < 0.05%', percentage: 2, statusColor: 'emerald' }
  ];

  // Deployment Pipeline Records
  const [deployments, setDeployments] = useState<DeploymentRecord[]>([
    {
      id: 'dep-001',
      timestampUtc: '2026-08-27 15:42:10',
      environment: 'Production',
      version: 'v2.5.1',
      status: 'Success',
      branch: 'main',
      actor: 'automated_ci',
      commitSha: '7a4f91b',
      canRollback: true,
      rollbackVersion: 'v2.5.0'
    },
    {
      id: 'dep-002',
      timestampUtc: '2026-08-27 14:15:00',
      environment: 'Staging',
      version: 'v2.5.2-rc1',
      status: 'In Progress',
      branch: 'release/2.5.2',
      actor: 'j.doe',
      commitSha: '9c1e302',
      canRollback: false
    },
    {
      id: 'dep-003',
      timestampUtc: '2026-08-27 12:30:45',
      environment: 'Development',
      version: 'feature-auth',
      status: 'Failed',
      branch: 'feat/new-auth',
      actor: 'a.smith',
      commitSha: '1b80d4f',
      canRollback: false
    },
    {
      id: 'dep-004',
      timestampUtc: '2026-08-26 18:20:00',
      environment: 'Production',
      version: 'v2.5.0',
      status: 'Success',
      branch: 'main',
      actor: 'sre_ops',
      commitSha: '4d2e90f',
      canRollback: true,
      rollbackVersion: 'v2.4.9'
    }
  ]);

  const triggerToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const handleRefresh = () => {
    const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setLastUpdated(`at ${timeStr}`);
    triggerToast('Health & deployment metrics refreshed.');
  };

  const filteredDeployments = deployments.filter((dep) => {
    if (envFilter === 'ALL') return true;
    return dep.environment === envFilter;
  });

  const handleCreateDeployment = (e: React.FormEvent) => {
    e.preventDefault();
    const newDep: DeploymentRecord = {
      id: `dep-${Date.now()}`,
      timestampUtc: new Date().toISOString().replace('T', ' ').substring(0, 19),
      environment: deployEnv,
      version: deployEnv === 'Production' ? 'v2.5.2' : 'v2.5.2-rc2',
      status: 'In Progress',
      branch: deployBranch,
      actor: 'SuperAdmin',
      commitSha: 'a8e9102',
      canRollback: false
    };

    setDeployments([newDep, ...deployments]);
    setShowNewDeploymentModal(false);
    triggerToast(`Deployment triggered for ${deployEnv} (${deployBranch})`);

    // Simulate completion after 4 seconds
    setTimeout(() => {
      setDeployments((prev) =>
        prev.map((d) => (d.id === newDep.id ? { ...d, status: 'Success', canRollback: true } : d))
      );
      triggerToast(`Deployment ${newDep.version} completed successfully on ${deployEnv}`);
    }, 4000);
  };

  const handleConfirmRollback = () => {
    if (!selectedRollback) return;
    const targetVer = selectedRollback.rollbackVersion || 'v2.5.0';
    triggerToast(`Initiating rollback to ${targetVer} for Production...`);
    
    // Add rollback record
    const rollbackRecord: DeploymentRecord = {
      id: `dep-rb-${Date.now()}`,
      timestampUtc: new Date().toISOString().replace('T', ' ').substring(0, 19),
      environment: 'Production',
      version: `${targetVer}-rollback`,
      status: 'In Progress',
      branch: 'rollback-target',
      actor: 'SuperAdmin (Rollback)',
      commitSha: 'rb-3901a',
      canRollback: false
    };

    setDeployments([rollbackRecord, ...deployments]);
    setSelectedRollback(null);

    setTimeout(() => {
      setDeployments((prev) =>
        prev.map((d) => (d.id === rollbackRecord.id ? { ...d, status: 'Success' } : d))
      );
      triggerToast(`Rollback to ${targetVer} completed.`);
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex text-[#1E293B] font-sans antialiased">
      {/* =============================================================== */}
      {/* 1. LEFT SIDEBAR (7 Canonical Domains)                           */}
      {/* =============================================================== */}
      <aside className="w-64 bg-white border-r border-[#E2E8F0] flex flex-col shrink-0 min-h-screen sticky top-0 h-screen overflow-y-auto">
        {/* Brand Header */}
        <div className="p-5 border-b border-[#F1F5F9] flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#0F172A] flex items-center justify-center text-white shadow-xs font-black text-lg">
            <YIcon />
          </div>
          <div>
            <h1 className="text-base font-extrabold text-[#0F172A] tracking-tight leading-none">
              Control Center
            </h1>
            <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider block mt-1">
              Administrator
            </span>
          </div>
        </div>

        {/* Sidebar Navigation Links */}
        <div className="flex-1 py-4 px-3 space-y-6">
          {/* Section: CORE */}
          <div>
            <div className="px-3 mb-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              CORE
            </div>
            <nav className="space-y-1">
              <button
                onClick={() => {
                  setSelectedDomain('overview');
                  onNavigateToView('admin-super-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'overview'
                    ? 'bg-[#2563EB] text-white font-bold shadow-xs'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Layers className="w-4 h-4" />
                <span>Overview</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('customers');
                  onNavigateToView('admin-organizations');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'customers'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Users className="w-4 h-4" />
                <span>Customers & Support</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('product');
                  onNavigateToView('cpo-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'product'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                <span>Product & Growth</span>
              </button>
            </nav>
          </div>

          {/* Section: PERFORMANCE */}
          <div>
            <div className="px-3 mb-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              PERFORMANCE
            </div>
            <nav className="space-y-1">
              <button
                onClick={() => {
                  setSelectedDomain('revenue');
                  onNavigateToView('cfo-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'revenue'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <DollarSign className="w-4 h-4" />
                <span>Revenue & Finance</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('data');
                  onNavigateToView('cdo-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'data'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Database className="w-4 h-4" />
                <span>Data & AI</span>
              </button>
            </nav>
          </div>

          {/* Section: SYSTEM */}
          <div>
            <div className="px-3 mb-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              SYSTEM
            </div>
            <nav className="space-y-1">
              <button
                onClick={() => {
                  setSelectedDomain('operations');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  selectedDomain === 'operations'
                    ? 'bg-[#2563EB] text-white shadow-xs'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <ShieldAlert className="w-4 h-4" />
                <span>Operations & Risk</span>
              </button>

              {/* Sub-navigation under Operations & Risk */}
              {selectedDomain === 'operations' && (
                <div className="ml-4 pl-3 border-l-2 border-[#2563EB] my-1 space-y-1">
                  <button
                    onClick={() => onNavigateToView('admin-system-health')}
                    className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
                  >
                    System Overview
                  </button>
                  <button
                    onClick={() => {}}
                    className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-bold text-[#2563EB] bg-blue-50 transition-colors cursor-pointer"
                  >
                    Health & Deployments
                  </button>
                </div>
              )}

              <button
                onClick={() => {
                  setSelectedDomain('configuration');
                  onNavigateToView('admin-roles-permissions');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'configuration'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Sliders className="w-4 h-4" />
                <span>Configuration</span>
              </button>
            </nav>
          </div>
        </div>

        {/* Sidebar Footer Profile */}
        <div className="p-3 border-t border-[#F1F5F9] space-y-2">
          <div className="flex items-center gap-3 p-2 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
            <div className="w-8 h-8 rounded-full bg-[#0F172A] text-emerald-400 font-bold text-xs flex items-center justify-center border border-emerald-500/30">
              AU
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-xs font-bold text-[#0F172A] block truncate">Admin User</span>
              <span className="text-[10px] font-mono text-[#64748B] block truncate">admin@yamoopay.com</span>
            </div>
          </div>
        </div>
      </aside>

      {/* =============================================================== */}
      {/* 2. MAIN CONTENT AREA                                           */}
      {/* =============================================================== */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* TOPBAR */}
        <header className="h-16 bg-white border-b border-[#E2E8F0] px-6 flex items-center justify-between sticky top-0 z-20 shadow-2xs">
          {/* Global Search Button */}
          <div className="flex items-center gap-4 flex-1 max-w-md">
            <button
              onClick={onOpenSearch}
              className="w-full flex items-center justify-between px-3.5 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs text-[#64748B] hover:border-[#CBD5E1] transition-all cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-[#94A3B8]" />
                <span>Search resources, users, or action IDs (Demo)...</span>
              </div>
              <kbd className="px-1.5 py-0.5 bg-white border border-[#CBD5E1] rounded text-[10px] font-mono font-bold text-[#64748B]">
                ⌘K
              </kbd>
            </button>
          </div>

          {/* Topbar Utility Icons */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => triggerToast('Security posture: Enforced via TLS & OAuth Tokens')}
              className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-xl transition-colors cursor-pointer"
              title="Security Status"
            >
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
            </button>
            <button
              onClick={() => onNavigateToView('admin-audit')}
              className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-xl transition-colors cursor-pointer"
              title="Audit Logs"
            >
              <FileText className="w-4 h-4 text-[#64748B]" />
            </button>
            <button
              onClick={() => setShowNotificationsPopover(!showNotificationsPopover)}
              className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-xl transition-colors relative cursor-pointer"
              title="Notifications"
            >
              <Bell className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* PAGE BODY */}
        <main className="p-8 space-y-8 max-w-7xl mx-auto w-full">
          {/* Toast Notification */}
          {toastMessage && (
            <div className="fixed bottom-6 right-6 z-50 bg-[#0F172A] text-white text-xs font-bold px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2 animate-in slide-in-from-bottom-5 duration-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span>{toastMessage}</span>
            </div>
          )}

          {/* Header & Main Actions */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-black text-[#0F172A] tracking-tight">
                System Health & Deployments
              </h1>
              <p className="text-xs font-medium text-[#64748B] mt-1 flex items-center gap-2">
                <span>Real-time operational dimensions and system health metrics.</span>
                <span>•</span>
                <span>Last updated: <strong className="text-[#0F172A] font-semibold">{lastUpdated}</strong></span>
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                className="px-3.5 py-2 bg-white border border-[#E2E8F0] hover:bg-[#F8FAFC] text-[#334155] font-bold text-xs rounded-xl shadow-2xs flex items-center gap-2 transition-colors cursor-pointer"
              >
                <RefreshCw className="w-3.5 h-3.5 text-[#64748B]" />
                <span>Refresh</span>
              </button>

              <button
                onClick={() => setShowNewDeploymentModal(true)}
                className="px-4 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold text-xs rounded-xl shadow-xs flex items-center gap-2 transition-colors cursor-pointer"
              >
                <Plus className="w-4 h-4 text-white" />
                <span>New Deployment</span>
              </button>
            </div>
          </div>

          {/* =============================================================== */}
          {/* 3. SECTION 1: SERVICE HEALTH (Cards layout per design)         */}
          {/* =============================================================== */}
          <div className="space-y-4">
            <h2 className="text-base font-extrabold text-[#0F172A]">
              Service Health
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {services.map((srv) => (
                <div
                  key={srv.id}
                  className={`bg-white border rounded-2xl p-4 shadow-2xs transition-all space-y-3 ${
                    srv.isDegraded
                      ? 'border-red-200 bg-red-50/20 hover:border-red-300'
                      : 'border-[#E2E8F0] hover:border-[#CBD5E1]'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className={`text-sm font-black ${srv.isDegraded ? 'text-red-700' : 'text-[#0F172A]'}`}>
                      {srv.name}
                    </h3>
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[11px] font-bold rounded-full ${
                        srv.isDegraded
                          ? 'bg-red-100 text-red-700'
                          : 'bg-emerald-50 text-emerald-700'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${srv.isDegraded ? 'bg-red-600 animate-ping' : 'bg-emerald-500'}`} />
                      {srv.status}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-xs pt-1 border-t border-[#F1F5F9]">
                    <span className="text-[#64748B] font-medium">
                      Latency: <strong className={srv.isDegraded ? 'text-red-600 font-bold' : 'text-[#0F172A] font-extrabold'}>{srv.latency}</strong>
                    </span>
                    <span className="text-[#64748B] font-medium">
                      Load: <strong className={srv.isDegraded ? 'text-red-600 font-bold' : 'text-[#0F172A] font-extrabold'}>{srv.load}</strong>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* =============================================================== */}
          {/* 4. ACTIVE ALERTS & INFRASTRUCTURE METRICS                       */}
          {/* =============================================================== */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Active Alerts Banner / Section (7 Cols) */}
            <div className="lg:col-span-7 space-y-4">
              <h2 className="text-base font-extrabold text-[#0F172A]">
                Active Operational Alerts
              </h2>

              <div className="bg-amber-50/60 border border-amber-200 rounded-2xl p-5 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-100 text-amber-800 rounded-xl shrink-0">
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-black text-amber-900 uppercase tracking-wide">
                        [WARNING] PostgreSQL DB Query Latency Spike
                      </span>
                      <span className="text-[10px] font-mono text-amber-700">12m ago</span>
                    </div>
                    <p className="text-xs text-amber-800 leading-relaxed">
                      Primary DB connection pool at 92/100 capacity. Average query latency elevated to 845ms (threshold &gt; 500ms).
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-amber-200/60 text-xs">
                  <a
                    href="https://runbooks.yamoopay.internal/postgresql-latency-triage"
                    target="_blank"
                    rel="noreferrer"
                    className="font-bold text-[#2563EB] hover:underline flex items-center gap-1"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>View PostgreSQL Triage Runbook</span>
                  </a>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => triggerToast('Alert acknowledged by SuperAdmin')}
                      className="px-3 py-1.5 bg-white border border-amber-300 text-amber-900 font-bold rounded-lg text-xs hover:bg-amber-100 transition-colors cursor-pointer"
                    >
                      Acknowledge Alert
                    </button>
                  </div>
                </div>
              </div>

              {/* Security & Dokploy Container Posture */}
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-2xs space-y-3">
                <div className="flex items-center justify-between border-b border-[#F1F5F9] pb-3">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-emerald-600" />
                    <h3 className="text-xs font-extrabold text-[#0F172A] uppercase tracking-wide">
                      Security & Secrets Masking
                    </h3>
                  </div>
                  <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 font-bold text-[10px] rounded">
                    Strict Masking Active
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="p-2.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl font-mono">
                    <span className="text-[#64748B] block text-[10px] uppercase font-sans">DATABASE_URL</span>
                    <span className="text-[#0F172A] font-bold">postgresql://app_usr:••••••••@10.0.0.5:5432</span>
                  </div>
                  <div className="p-2.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl font-mono">
                    <span className="text-[#64748B] block text-[10px] uppercase font-sans">GEMINI_API_KEY</span>
                    <span className="text-[#0F172A] font-bold">AIzaSy••••••••••••••••••••38</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Infrastructure Metrics Grid (5 Cols) */}
            <div className="lg:col-span-5 space-y-4">
              <h2 className="text-base font-extrabold text-[#0F172A]">
                Infrastructure Metrics
              </h2>

              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-2xs space-y-4">
                {infraMetrics.map((metric) => (
                  <div key={metric.label} className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-extrabold text-[#0F172A]">{metric.label}</span>
                      <span className="font-mono font-bold text-[#0F172A]">{metric.value}</span>
                    </div>
                    <div className="w-full h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          metric.percentage > 85
                            ? 'bg-amber-500'
                            : metric.percentage > 95
                            ? 'bg-red-500'
                            : 'bg-[#2563EB]'
                        }`}
                        style={{ width: `${Math.max(metric.percentage, 4)}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-[#64748B] font-medium block">
                      {metric.subtext}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* =============================================================== */}
          {/* 5. DEPLOYMENT PIPELINE & RECENT DEPLOYMENTS TABLE               */}
          {/* =============================================================== */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xs space-y-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-[#F1F5F9] pb-4">
              <div className="flex items-center gap-2.5">
                <GitBranch className="w-5 h-5 text-[#2563EB]" />
                <h2 className="text-base font-extrabold text-[#0F172A]">
                  Recent Deployments & Pipeline History
                </h2>
              </div>

              {/* Environment Filter Buttons */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#64748B] font-bold">Env:</span>
                {(['ALL', 'Production', 'Staging', 'Development'] as const).map((env) => (
                  <button
                    key={env}
                    onClick={() => setEnvFilter(env)}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      envFilter === env
                        ? 'bg-[#0F172A] text-white'
                        : 'bg-[#F1F5F9] text-[#64748B] hover:text-[#0F172A]'
                    }`}
                  >
                    {env}
                  </button>
                ))}
              </div>
            </div>

            {/* Deployments Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-[#F1F5F9] text-[#64748B]">
                    <th className="pb-3 font-semibold">Timestamp (UTC)</th>
                    <th className="pb-3 font-semibold">Environment</th>
                    <th className="pb-3 font-semibold">Version</th>
                    <th className="pb-3 font-semibold">Status</th>
                    <th className="pb-3 font-semibold">Branch</th>
                    <th className="pb-3 font-semibold">Actor</th>
                    <th className="pb-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {filteredDeployments.map((dep) => (
                    <tr key={dep.id} className="hover:bg-[#F8FAFC] transition-colors">
                      <td className="py-3.5 font-mono text-[#475569]">{dep.timestampUtc}</td>
                      <td className="py-3.5">
                        <span
                          className={`px-2.5 py-1 text-[11px] font-extrabold rounded-md ${
                            dep.environment === 'Production'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : dep.environment === 'Staging'
                              ? 'bg-amber-50 text-amber-700 border border-amber-200'
                              : 'bg-blue-50 text-blue-700 border border-blue-200'
                          }`}
                        >
                          {dep.environment}
                        </span>
                      </td>
                      <td className="py-3.5 font-extrabold text-[#0F172A]">{dep.version}</td>
                      <td className="py-3.5">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[11px] font-extrabold rounded-full ${
                            dep.status === 'Success'
                              ? 'bg-emerald-100 text-emerald-800'
                              : dep.status === 'In Progress'
                              ? 'bg-blue-100 text-blue-800 animate-pulse'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {dep.status === 'Success' && <Check className="w-3 h-3 text-emerald-600" />}
                          {dep.status === 'In Progress' && <RotateCcw className="w-3 h-3 text-blue-600 animate-spin" />}
                          {dep.status === 'Failed' && <X className="w-3 h-3 text-red-600" />}
                          {dep.status}
                        </span>
                      </td>
                      <td className="py-3.5 font-mono text-[#64748B]">{dep.branch}</td>
                      <td className="py-3.5 font-semibold text-[#334155]">{dep.actor}</td>
                      <td className="py-3.5 text-right space-x-2">
                        {dep.canRollback && dep.environment === 'Production' && (
                          <button
                            onClick={() => setSelectedRollback(dep)}
                            className="px-2.5 py-1 bg-red-50 hover:bg-red-100 text-red-700 font-bold text-[11px] rounded-lg transition-colors cursor-pointer inline-flex items-center gap-1"
                          >
                            <RotateCcw className="w-3 h-3" />
                            <span>Rollback</span>
                          </button>
                        )}
                        <a
                          href="https://runbooks.yamoopay.internal/deployment-audit"
                          target="_blank"
                          rel="noreferrer"
                          className="px-2.5 py-1 bg-[#F1F5F9] hover:bg-[#E2E8F0] text-[#334155] font-bold text-[11px] rounded-lg transition-colors cursor-pointer inline-flex items-center gap-1"
                        >
                          <span>Runbook</span>
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {/* =============================================================== */}
      {/* MODAL: NEW DEPLOYMENT PIPELINE TRIGGER                          */}
      {/* =============================================================== */}
      {showNewDeploymentModal && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-2xl max-w-lg w-full p-6 space-y-5 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-[#F1F5F9] pb-3">
              <div className="flex items-center gap-2">
                <GitBranch className="w-5 h-5 text-[#2563EB]" />
                <h3 className="text-base font-bold text-[#0F172A]">Trigger New Deployment</h3>
              </div>
              <button
                onClick={() => setShowNewDeploymentModal(false)}
                className="p-1 text-[#94A3B8] hover:text-[#0F172A] rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateDeployment} className="space-y-4 text-xs">
              <div>
                <label className="font-extrabold text-[#0F172A] block mb-1">Target Environment</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setDeployEnv('Staging')}
                    className={`p-3 rounded-xl border text-center font-bold transition-all cursor-pointer ${
                      deployEnv === 'Staging'
                        ? 'bg-amber-50 border-amber-300 text-amber-900 ring-2 ring-amber-400'
                        : 'bg-[#F8FAFC] border-[#E2E8F0] text-[#64748B]'
                    }`}
                  >
                    Staging
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeployEnv('Production')}
                    className={`p-3 rounded-xl border text-center font-bold transition-all cursor-pointer ${
                      deployEnv === 'Production'
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-900 ring-2 ring-emerald-400'
                        : 'bg-[#F8FAFC] border-[#E2E8F0] text-[#64748B]'
                    }`}
                  >
                    Production (Step-up Auth)
                  </button>
                </div>
              </div>

              <div>
                <label className="font-extrabold text-[#0F172A] block mb-1">Git Branch / Artifact Tag</label>
                <input
                  type="text"
                  value={deployBranch}
                  onChange={(e) => setDeployBranch(e.target.value)}
                  className="w-full px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl font-mono text-xs focus:ring-2 focus:ring-[#2563EB] outline-none"
                  placeholder="e.g. main, release/2.5.2"
                  required
                />
              </div>

              <div>
                <label className="font-extrabold text-[#0F172A] block mb-1">Release Summary / Change Notes</label>
                <textarea
                  value={deployNotes}
                  onChange={(e) => setDeployNotes(e.target.value)}
                  className="w-full px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs focus:ring-2 focus:ring-[#2563EB] outline-none h-20"
                  placeholder="Describe the purpose of this release..."
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#F1F5F9]">
                <button
                  type="button"
                  onClick={() => setShowNewDeploymentModal(false)}
                  className="px-4 py-2 bg-[#F1F5F9] hover:bg-[#E2E8F0] text-[#0F172A] font-bold text-xs rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer flex items-center gap-1.5"
                >
                  <Play className="w-3.5 h-3.5 fill-current" />
                  <span>Execute Deployment</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* MODAL: ROLLBACK CONFIRMATION                                    */}
      {/* =============================================================== */}
      {selectedRollback && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-2xl max-w-md w-full p-6 space-y-5 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-[#F1F5F9] pb-3">
              <div className="flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-red-600" />
                <h3 className="text-base font-bold text-[#0F172A]">Confirm Production Rollback</h3>
              </div>
              <button
                onClick={() => setSelectedRollback(null)}
                className="p-1 text-[#94A3B8] hover:text-[#0F172A] rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <p className="text-[#334155] leading-relaxed">
                Are you sure you want to revert production release <strong className="text-[#0F172A]">{selectedRollback.version}</strong> back to target artifact <strong className="text-red-700">{selectedRollback.rollbackVersion || 'v2.5.0'}</strong>?
              </p>
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-800 space-y-1">
                <span className="font-extrabold block">Rollback Protocol Safeguards:</span>
                <ul className="list-disc pl-4 space-y-0.5 text-[11px]">
                  <li>Traffic will be re-routed to stable container image.</li>
                  <li>Database migrations will remain forward-compatible.</li>
                  <li>Audit log entry will record this action.</li>
                </ul>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#F1F5F9]">
              <button
                onClick={() => setSelectedRollback(null)}
                className="px-4 py-2 bg-[#F1F5F9] hover:bg-[#E2E8F0] text-[#0F172A] font-bold text-xs rounded-xl cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRollback}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer"
              >
                Execute Rollback
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function YIcon() {
  return (
    <span className="font-black text-white text-base leading-none">
      Y
    </span>
  );
}
